import { Button, Flex, Layout, Segmented, Spin, Typography } from "antd"
import dayjs from "dayjs"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"

import { EducationAudioPlayer } from "../../components/Education/EducationAudioPlayer.tsx"
import { extractPlainText } from "../Lead/LeadWorkspacePage/education/education-track-helpers.ts"
import type { CitationView } from "../Lead/LeadWorkspacePage/education/types/education.types.ts"
import {
  fetchEducationGuide,
  fetchEducationPublicAudio,
  resolveEducationLink,
  sendEducationPublicEvent
} from "./api/public-education.services"
import { CustomerMorePanel } from "./components/CustomerMorePanel.tsx"
import { GuideUnavailable } from "./components/GuideUnavailable.tsx"
import { ProjectEducationGuideBody } from "./components/ProjectEducationGuideBody.tsx"
import { VerifyEmailForm } from "./components/VerifyEmailForm.tsx"
import {
  clearStoredEducationSession,
  readStoredEducationSession,
  writeStoredEducationSession
} from "./education-session-storage"
import {
  getStoredAudioPositionMs,
  storeAudioPositionMs
} from "./local-audio-progress"
import type {
  PublicCitation,
  PublicEdition,
  PublicGuide
} from "./types/public-education.types"

const { Text } = Typography

type Stage = "resolving" | "verify" | "guide" | "unavailable"

const PUBLIC_DATE_FORMAT = "MMM D, YYYY"

const toCitationView = (citation: PublicCitation): CitationView => ({
  number: citation.number,
  // The public serializer never sends a row id (see `public_serializers.py`'s
  // `_citations`) — the edition's own citation numbering is stable and
  // unique within it, so it doubles as a React key here.
  id: String(citation.number),
  label: citation.label,
  url: citation.url,
  kind: citation.kind
})

/** How often a "being prepared" guide asks again whether it is ready. */
const PREPARING_POLL_MS = 10_000
/** Longest wait between two asks after failed ones. */
const PREPARING_MAX_BACKOFF_MS = 60_000
/** Asks before the page stops on its own and offers "Check again". */
const PREPARING_MAX_POLLS = 60

/** The Overview edition (the lowest `number` — `EDITION_LABELS` reserves `1`
 *  for it) — by `number`, not array order, which `sharing.visible_editions`
 *  never promises. A fresh visit lands here even when the sales team already
 *  generated Section 2 before sharing the link; asking for more
 *  (`CustomerMorePanel`) is what selects a later edition explicitly. */
const defaultPublicEditionId = (editions: PublicEdition[]): string | null => {
  if (editions.length === 0) return null
  return editions.reduce((oldest, edition) =>
    edition.number < oldest.number ? edition : oldest
  ).public_id
}

/**
 * Screens 7 and 8 — the page a customer reaches from a share link, with no
 * account and no app shell. Three things distinguish it from every other
 * screen in this app: it sends no auth header (a bare `publicApi`, not the
 * signed-in client, so a colleague opening the same link sees exactly what
 * the customer sees), it never learns the lead's name for its own title bar
 * or `document.title` (a stranger holding a dead link learns nothing), and
 * the token it arrives with never leaves this component except in the one
 * `resolve/` call it's exchanged in — every route after that carries the
 * opaque session the exchange returned.
 */
export function ProjectEducationPage() {
  const { t } = useTranslation()
  const { hash } = useLocation()
  const openingRef = useRef<string | null>(null)
  const startedAudioIdsRef = useRef<Set<string>>(new Set())
  const pendingSelectRef = useRef<{ wanted: string | null } | null>(null)

  const [stage, setStage] = useState<Stage>("resolving")
  const [session, setSession] = useState<string | null>(null)
  const [guide, setGuide] = useState<PublicGuide | null>(null)
  const [selectedEditionId, setSelectedEditionId] = useState<string | null>(
    null
  )
  const [guideRefreshToken, setGuideRefreshToken] = useState(0)
  const [preparingStopped, setPreparingStopped] = useState(false)
  const guideRef = useRef<PublicGuide | null>(null)
  const pollCountRef = useRef(0)
  const failureCountRef = useRef(0)

  // `document.title` never reflects the lead — see spec §10, "browser
  // history titles" — and no crawler or referrer should ever learn this page
  // exists at all.
  useEffect(() => {
    const previousTitle = document.title
    document.title = t("projectEducation.publicPage.documentTitle")
    return () => {
      document.title = previousTitle
    }
  }, [t])

  useEffect(() => {
    const robots = document.createElement("meta")
    robots.name = "robots"
    robots.content = "noindex"
    const referrer = document.createElement("meta")
    referrer.name = "referrer"
    referrer.content = "no-referrer"
    document.head.appendChild(robots)
    document.head.appendChild(referrer)
    return () => {
      document.head.removeChild(robots)
      document.head.removeChild(referrer)
    }
  }, [])

  const goUnavailable = useCallback((status?: number) => {
    // Only a definite "this token/session is dead" clears the stored
    // session — a network blip or a 5xx keeps it so a reload can try again,
    // the same rule `CustomerUploadPage` follows for its own stored token.
    if (status === 404) clearStoredEducationSession()
    setStage("unavailable")
  }, [])

  // Resolve on mount: a fragment present means a fresh visit (exchange the
  // token, once, then drop it from the address bar immediately — regardless
  // of how the exchange turns out, so it never lingers there across the
  // round trip); no fragment means a reload, which falls back to whatever
  // session an earlier visit this tab already stored.
  useEffect(() => {
    const fromHash = hash.replace(/^#/, "")

    if (fromHash) {
      if (openingRef.current === fromHash) return
      openingRef.current = fromHash
      window.history.replaceState(null, "", window.location.pathname)
      setStage("resolving")

      void resolveEducationLink(fromHash)
        .then((response) => {
          if (!response.ok || !response.data) {
            goUnavailable(response.status)
            return
          }
          const { session: newSession, next } = response.data
          writeStoredEducationSession({ session: newSession, next })
          setSession(newSession)
          setStage(next)
        })
        .catch(() => goUnavailable())
      return
    }

    if (openingRef.current === "stored") return
    openingRef.current = "stored"
    const stored = readStoredEducationSession()
    if (!stored) {
      setStage("unavailable")
      return
    }
    setSession(stored.session)
    setStage(stored.next)
  }, [hash, goUnavailable])

  // Fetch (or refetch, on a `CustomerMorePanel` settle) the guide once a
  // verified session is in hand. While it is being prepared, ask again every
  // 10 seconds: only while the tab is visible, backing off after a failed
  // ask, and stopping after PREPARING_MAX_POLLS asks.
  useEffect(() => {
    if (stage !== "guide" || !session) return
    let cancelled = false
    let pollTimer: ReturnType<typeof setTimeout> | null = null
    let waitingForVisible = false

    const askAgain = () => setGuideRefreshToken((value) => value + 1)
    const onVisible = () => {
      if (document.hidden) return
      document.removeEventListener("visibilitychange", onVisible)
      waitingForVisible = false
      askAgain()
    }
    const scheduleNextAsk = (delayMs: number) => {
      if (pollCountRef.current >= PREPARING_MAX_POLLS) {
        setPreparingStopped(true)
        return
      }
      pollTimer = setTimeout(() => {
        if (document.hidden) {
          waitingForVisible = true
          document.addEventListener("visibilitychange", onVisible)
          return
        }
        askAgain()
      }, delayMs)
    }

    pollCountRef.current += 1
    void fetchEducationGuide(session).then((response) => {
      if (cancelled) return
      if (!response.ok || !response.data) {
        const current = guideRef.current
        const stillWaiting =
          !!current && (current.preparing || current.editions.length === 0)
        // A dropped connection, a throttle or a server error while waiting is
        // not a dead link; only a 404 is.
        if (stillWaiting && response.status !== 404) {
          failureCountRef.current += 1
          scheduleNextAsk(
            Math.min(
              PREPARING_POLL_MS * 2 ** (failureCountRef.current - 1),
              PREPARING_MAX_BACKOFF_MS
            )
          )
          return
        }
        if (current && response.status !== 404) return
        goUnavailable(response.status)
        return
      }
      failureCountRef.current = 0
      const data = response.data
      guideRef.current = data
      setGuide(data)
      if (data.preparing || data.editions.length === 0) {
        scheduleNextAsk(PREPARING_POLL_MS)
      } else {
        pollCountRef.current = 0
      }

      const pending = pendingSelectRef.current
      pendingSelectRef.current = null
      const wanted = pending?.wanted
      if (
        wanted &&
        data.editions.some((edition) => edition.public_id === wanted)
      ) {
        setSelectedEditionId(wanted)
        return
      }
      setSelectedEditionId((current) => {
        if (
          current &&
          data.editions.some((edition) => edition.public_id === current)
        )
          return current
        return defaultPublicEditionId(data.editions)
      })
    })

    return () => {
      cancelled = true
      if (pollTimer) clearTimeout(pollTimer)
      if (waitingForVisible)
        document.removeEventListener("visibilitychange", onVisible)
    }
    // `guideRefreshToken` is a plain counter with no meaning of its own — its
    // only job is to make this effect re-run on demand.
  }, [stage, session, guideRefreshToken, goUnavailable])

  const handleVerified = useCallback((newSession: string) => {
    writeStoredEducationSession({ session: newSession, next: "guide" })
    setSession(newSession)
    setStage("guide")
  }, [])

  const checkAgain = useCallback(() => {
    pollCountRef.current = 0
    setPreparingStopped(false)
    setGuideRefreshToken((value) => value + 1)
  }, [])

  const refreshGuide = useCallback((selectPublicId?: string) => {
    pendingSelectRef.current = { wanted: selectPublicId ?? null }
    setGuideRefreshToken((value) => value + 1)
  }, [])

  const currentEdition =
    guide?.editions.find(
      (edition) => edition.public_id === selectedEditionId
    ) ?? null

  const handleContactClick = () => {
    if (session) sendEducationPublicEvent(session, { type: "contact_clicked" })
  }

  const handleAudioPlay = (publicId: string) => {
    if (startedAudioIdsRef.current.has(publicId)) return
    startedAudioIdsRef.current.add(publicId)
    if (session)
      sendEducationPublicEvent(session, {
        type: "audio_started",
        public_id: publicId
      })
  }

  const handleAudioProgress = (
    publicId: string,
    positionMs: number,
    completed: boolean
  ) => {
    storeAudioPositionMs(publicId, positionMs)
    if (!session) return
    sendEducationPublicEvent(session, {
      type: "audio_progress",
      public_id: publicId,
      position_ms: Math.round(positionMs)
    })
    if (completed) {
      sendEducationPublicEvent(session, {
        type: "audio_completed",
        public_id: publicId
      })
    }
  }

  const audioLabels = {
    play: t("projectEducation.audioPlayer.play"),
    pause: t("projectEducation.audioPlayer.pause"),
    back15: t("projectEducation.audioPlayer.back15"),
    restart: t("projectEducation.audioPlayer.restart"),
    speed: t("projectEducation.audioPlayer.speed"),
    preparing: t("projectEducation.audioPlayer.preparing"),
    audioReady: t("projectEducation.audioPlayer.audioReady"),
    autoplayBlocked: t("projectEducation.audioPlayer.autoplayBlocked"),
    failed: t("projectEducation.audioPlayer.failed"),
    retry: t("projectEducation.audioPlayer.retry"),
    transcriptShow: t("projectEducation.audioPlayer.transcriptShow"),
    transcriptHide: t("projectEducation.audioPlayer.transcriptHide"),
    back15Short: t("projectEducation.audioPlayer.back15Short"),
    seek: t("projectEducation.audioPlayer.seek"),
    elapsedSeconds: (count: number) =>
      t("projectEducation.audioPlayer.elapsedSeconds", { count })
  }

  const topBarLabel =
    guide?.company_name || t("projectEducation.publicPage.brand")

  const renderGuide = () => {
    if (!guide) {
      return (
        <Flex justify={"center"} className={"py-16"}>
          <Spin size={"large"} />
        </Flex>
      )
    }

    if (guide.preparing || guide.editions.length === 0) {
      return (
        <Flex
          vertical
          align={"center"}
          gap={12}
          className={"py-16 text-center"}
          role={"status"}
        >
          {!preparingStopped && <Spin size={"large"} />}
          <Typography.Title level={3} className={"!mb-0"}>
            {t("projectEducation.publicPage.preparing.title")}
          </Typography.Title>
          <Typography.Text type={"secondary"}>
            {preparingStopped
              ? t("projectEducation.publicPage.preparing.stoppedBody")
              : t("projectEducation.publicPage.preparing.body")}
          </Typography.Text>
          {preparingStopped && (
            <Button onClick={checkAgain}>
              {t("projectEducation.publicPage.preparing.checkAgain")}
            </Button>
          )}
        </Flex>
      )
    }

    const hasCustomerMore = guide.capabilities.includes("customer_more")
    const hasAudio =
      !!currentEdition && currentEdition.audio.status !== "unavailable"

    return (
      <ProjectEducationGuideBody
        projectLabel={guide.project_label}
        preparedFor={guide.prepared_for}
        sharedBy={guide.shared_by}
        updatedLabel={
          guide.updated_at
            ? dayjs(guide.updated_at).format(PUBLIC_DATE_FORMAT)
            : null
        }
        html={currentEdition?.html ?? ""}
        citations={(currentEdition?.citations ?? []).map(toCitationView)}
        limitations={currentEdition?.limitations ?? []}
        editionSelector={
          guide.editions.length > 1 ? (
            <Segmented
              className={"mb-3"}
              value={selectedEditionId ?? undefined}
              onChange={(value) => setSelectedEditionId(value as string)}
              options={guide.editions.map((edition) => ({
                label: t(
                  `projectEducation.publicPage.editionLabels.${edition.number}`,
                  {
                    defaultValue: edition.label
                  }
                ),
                value: edition.public_id
              }))}
            />
          ) : undefined
        }
        audioSlot={
          hasAudio && currentEdition ? (
            <EducationAudioPlayer
              key={currentEdition.public_id}
              fetchAudio={() =>
                fetchEducationPublicAudio(session!, currentEdition.public_id)
              }
              initialPositionMs={getStoredAudioPositionMs(
                currentEdition.public_id
              )}
              onPlay={() => handleAudioPlay(currentEdition.public_id)}
              onProgress={(positionMs, completed) =>
                handleAudioProgress(
                  currentEdition.public_id,
                  positionMs,
                  completed
                )
              }
              transcript={extractPlainText(currentEdition.html)}
              title={t("projectEducation.publicPage.documentTitle")}
              labels={audioLabels}
            />
          ) : undefined
        }
        footerSlot={
          <Flex vertical gap={16} className={"mt-4"}>
            {hasCustomerMore && session && (
              <CustomerMorePanel
                session={session}
                remaining={guide.more_remaining}
                onGuideShouldRefresh={refreshGuide}
              />
            )}
            {guide.contact_email && (
              <Flex gap={16} wrap className={"text-sm"}>
                <a
                  href={`mailto:${encodeURIComponent(guide.contact_email)}`}
                  onClick={handleContactClick}
                >
                  {t("projectEducation.publicPage.footer.contact", {
                    name:
                      guide.shared_by ||
                      t(
                        "projectEducation.publicPage.footer.contactFallbackName"
                      )
                  })}
                </a>
                <a
                  href={`mailto:${encodeURIComponent(guide.contact_email)}?subject=${encodeURIComponent(
                    t("projectEducation.publicPage.footer.reportIssueSubject", {
                      projectLabel: guide.project_label
                    })
                  )}`}
                >
                  {t("projectEducation.publicPage.footer.reportIssue")}
                </a>
              </Flex>
            )}
          </Flex>
        }
        labels={{
          guideTitle: (label) =>
            t("projectEducation.guideBody.titleFor", { projectLabel: label }),
          preparedFor: t("projectEducation.guideBody.preparedFor"),
          sharedBy: t("projectEducation.guideBody.sharedBy"),
          updated: t("projectEducation.guideBody.updated"),
          sourcesHeading: t("projectEducation.guideBody.sourcesHeading"),
          disclaimer: t("projectEducation.guideBody.disclaimer"),
          articleLabels: {
            viewSources: (count) =>
              t("projectEducation.article.viewSources", { count }),
            sourcesLabel: t("projectEducation.article.sourcesLabel")
          }
        }}
      />
    )
  }

  return (
    <Layout
      className={"min-h-svh bg-white"}
      data-testid={"project-education-page"}
    >
      <div className={"mx-auto w-full max-w-3xl px-4 py-6"}>
        <Text
          strong
          className={"mb-4 block text-lg"}
          data-testid={"project-education-topbar"}
        >
          {topBarLabel}
        </Text>

        {stage === "resolving" && (
          <Flex justify={"center"} className={"py-16"}>
            <Spin size={"large"} />
          </Flex>
        )}

        {stage === "verify" && session && (
          <VerifyEmailForm session={session} onVerified={handleVerified} />
        )}

        {stage === "guide" && renderGuide()}

        {stage === "unavailable" && <GuideUnavailable />}
      </div>
    </Layout>
  )
}

export default ProjectEducationPage
